/* ==========================================================================
   filepath: packages/volto-castle-theme/src/components/Blocks/HPHero/HPHero.tsx
   ========================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// IMPORTANT: import into a DIFFERENT variable name and normalize to string URL
import laptopCastleImport from '../../../static/img/laptop-castle.png';
import phoneTabletImport from '../../../static/img/castle-phone-tablet.png';

const laptopCastle: string | null =
  typeof laptopCastleImport === 'string'
    ? laptopCastleImport
    : (laptopCastleImport as any)?.default || null;

const phoneTablet: string | null =
  typeof phoneTabletImport === 'string'
    ? phoneTabletImport
    : (phoneTabletImport as any)?.default || null;

export type HPHeroSlide = {
  id: string;
  title: string;
  subtitle: string;
  primaryCtaLabel: string;
  primaryCtaHref: string;
  secondaryCtaLabel: string;
  secondaryCtaHref: string;
  imageSrc?: string | null;
  imageAlt?: string;
  layout?: 'center' | 'split';
};

type Props = {
  slides?: HPHeroSlide[];
  autoplayMs?: number; // 0 disables
  className?: string;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ICONS = {
  download: 'ion ion-ios-cloud-download-outline',
  play: 'ion ion-ios-play-outline',
};

type ChevronProps = {
  direction: 'left' | 'right';
  className?: string;
};

const Chevron: React.FC<ChevronProps> = ({ direction, className }) => {
  const isLeft = direction === 'left';
  return (
    <svg className={className} width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d={isLeft ? 'M14.5 5.5 8 12l6.5 6.5' : 'M9.5 5.5 16 12l-6.5 6.5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/**
 * Default slides
 * - 0 provided => use both defaults
 * - 1 provided => append default #2
 * - 2+ provided => use as-is
 */
const DEFAULT_SLIDES: HPHeroSlide[] = [
  {
    id: 'default-1',
    title: 'WELCOME TO CASTLECMS',
    subtitle: 'The powerful and extraordinarily secure content management experience.',
    primaryCtaLabel: 'DOWNLOAD',
    primaryCtaHref: 'https://github.com/castlecms/',
    secondaryCtaLabel: 'WATCH TUTORIAL',
    secondaryCtaHref: 'contact',
    imageSrc: phoneTablet,
    imageAlt: 'CastleCMS on phone and tablet',
    layout: 'center',
  },
  {
    id: 'default-2',
    title: 'BUILT FOR TEAMS',
    subtitle: 'Collaborate, review, and publish with confidence — at enterprise scale.',
    primaryCtaLabel: 'DOWNLOAD',
    primaryCtaHref: 'https://github.com/castlecms/',
    secondaryCtaLabel: 'LIVE DEMO',
    secondaryCtaHref: 'contact',
    imageSrc: laptopCastle,
    imageAlt: 'Castle Laptop',
    layout: 'split',
  },
];

const HPHero: React.FC<Props> = ({ slides, autoplayMs = 6000, className }) => {
  const safeSlides = useMemo<HPHeroSlide[]>(() => {
    if (!Array.isArray(slides) || slides.length === 0) return DEFAULT_SLIDES;

    if (slides.length === 1) {
      const first = slides[0];
      const baseSecond = DEFAULT_SLIDES[1];
      const second =
        baseSecond.id === first.id ? { ...baseSecond, id: `${baseSecond.id}-2` } : baseSecond;
      return [first, second];
    }

    return slides;
  }, [slides]);

  const [active, setActive] = useState(0);
  const [animDir, setAnimDir] = useState<'next' | 'prev'>('next');
  const reduceMotion = useRef(false);

  useEffect(() => {
    reduceMotion.current = prefersReducedMotion();
  }, []);

  useEffect(() => {
    if (active >= safeSlides.length) setActive(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeSlides.length]);

  const goTo = useCallback(
    (idx: number, dir: 'next' | 'prev' = 'next') => {
      const n = safeSlides.length;
      if (!n) return;
      setAnimDir(dir);
      setActive(((idx % n) + n) % n);
    },
    [safeSlides.length],
  );

  const next = useCallback(() => goTo(active + 1, 'next'), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1, 'prev'), [active, goTo]);

  useEffect(() => {
    if (!autoplayMs || autoplayMs < 1000) return;
    if (reduceMotion.current) return;
    if (safeSlides.length <= 1) return;

    const t = window.setInterval(() => {
      setAnimDir('next');
      setActive((v) => (v + 1) % safeSlides.length);
    }, autoplayMs);

    return () => window.clearInterval(t);
  }, [autoplayMs, safeSlides.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  if (!safeSlides.length) return null;

  return (
    <section
      className={`hp-hero ${className ?? ''}`.trim()}
      aria-roledescription="carousel"
      data-animdir={animDir}
    >
      <div className="hp-hero__inner">
        <button
          type="button"
          className="hp-hero__arrow hp-hero__arrow--left"
          onClick={prev}
          aria-label="Previous slide"
        >
          <Chevron direction="left" className="hp-hero__chev" />
        </button>

        <div className="hp-hero__viewport" aria-live="polite">
          <div
            className="hp-hero__track"
            style={
              {
                ['--hp-hero-active' as any]: active,
              } as React.CSSProperties
            }
          >
            {safeSlides.map((s, i) => {
              const isActive = i === active;
              const layout = s.layout ?? 'center';

              return (
                <div
                  key={s.id || `slide-${i}`}
                  className={`hp-hero__slide ${isActive ? 'is-active' : ''} hp-hero__slide--${layout}`}
                  aria-hidden={!isActive}
                >
                  {layout === 'split' ? (
                    <div className="hp-hero__split">
                      <div className="hp-hero__splitLeft">
                        <h1 className="hp-hero__title">{s.title}</h1>
                        <p className="hp-hero__subtitle">{s.subtitle}</p>

                        <div className="hp-hero__actions hp-hero__actions--split">
                          <a className="hp-hero__btn hp-hero__btn--primary" href={s.primaryCtaHref}>
                            <span className="hp-hero__btnText">{s.primaryCtaLabel}</span>
                            <span className="hp-hero__btnIcon" aria-hidden="true">
                              <i className={ICONS.download} aria-hidden="true" />
                            </span>
                          </a>

                          <a className="hp-hero__btn hp-hero__btn--ghostSolid" href={s.secondaryCtaHref}>
                            <span className="hp-hero__btnText">{s.secondaryCtaLabel}</span>
                            <span className="hp-hero__btnIcon" aria-hidden="true">
                              <i className="ion ion-ios-arrow-forward" aria-hidden="true" />
                            </span>
                          </a>
                        </div>
                      </div>

                      <div className="hp-hero__splitRight">
                        {s.imageSrc ? (
                          <img
                            className="hp-hero__laptopImg hp-hero__laptopImg--right"
                            src={s.imageSrc}
                            alt={s.imageAlt ?? ''}
                          />
                        ) : (
                          <div
                            className="hp-hero__laptopMock hp-hero__laptopMock--right"
                            role="img"
                            aria-label={s.imageAlt ?? 'Laptop'}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="hp-hero__content">
                      <h1 className="hp-hero__title">{s.title}</h1>
                      <p className="hp-hero__subtitle">{s.subtitle}</p>

                      <div className="hp-hero__actions">
                        <a className="hp-hero__btn hp-hero__btn--primary" href={s.primaryCtaHref}>
                          <span className="hp-hero__btnText">{s.primaryCtaLabel}</span>
                          <span className="hp-hero__btnIcon" aria-hidden="true">
                            <i className={ICONS.download} aria-hidden="true" />
                          </span>
                        </a>

                        <a className="hp-hero__btn hp-hero__btn--ghost" href={s.secondaryCtaHref}>
                          <span className="hp-hero__play" aria-hidden="true">
                            <i className={ICONS.play} aria-hidden="true" />
                          </span>
                          <span className="hp-hero__btnText">{s.secondaryCtaLabel}</span>
                        </a>
                      </div>

                      <div className="hp-hero__media">
                        {s.imageSrc ? (
                          <img className="hp-hero__laptopImg" src={s.imageSrc} alt={s.imageAlt ?? ''} />
                        ) : (
                          <div className="hp-hero__laptopMock" role="img" aria-label={s.imageAlt ?? 'Laptop'} />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="hp-hero__dots" role="tablist" aria-label="Slides">
          {safeSlides.map((s, i) => (
            <button
              key={s.id || `dot-${i}`}
              type="button"
              className={`hp-hero__dot ${i === active ? 'is-active' : ''}`}
              onClick={() => goTo(i, i > active ? 'next' : 'prev')}
              aria-label={`Go to slide ${i + 1}`}
              aria-pressed={i === active}
            />
          ))}
        </div>

        <button
          type="button"
          className="hp-hero__arrow hp-hero__arrow--right"
          onClick={next}
          aria-label="Next slide"
        >
          <Chevron direction="right" className="hp-hero__chev" />
        </button>
      </div>
    </section>
  );
};

export default HPHero;